/**
 * New asset → **From files** (`/assets/new?from=files`): upload 1 to 10 files, choose the kind and an optional hint,
 * and start an extraction; its status is asked for every 3 s (`&extraction=<id>` keeps it across a reload); once it
 * succeeds the proposal opens in the review editor. Nothing is in the catalog until the review is saved.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, FileSearch } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useAssetTemplate, useExtractionProposal, useExtractionStatus } from '@/api/queries';
import { LoadFailure, StateCard } from '@/ui/StateCard';

import '../files/files.css';
import { WaitingCard } from '../files/WaitingCard';
import { ReviewStep } from './ReviewStep';
import { UploadStep } from './UploadStep';

const RUNNING_TEXT = {
  queued: 'Waiting for the extraction to start…',
  running: 'Reading your files and drafting the asset. This takes a minute or two.',
};

function StartAgain({ onStartOver }: { onStartOver: () => void }) {
  return (
    <p className="empty-actions">
      <button type="button" className="btn btn-secondary" onClick={onStartOver}>Start again</button>
      <Link className="btn btn-ghost" to="/assets/new">Create it by hand</Link>
    </p>
  );
}

function Proposal({ id, onStartOver }: { id: string; onStartOver: () => void }) {
  const proposal = useExtractionProposal(id, true);
  const template = useAssetTemplate(proposal.data?.mode === 'extract' ? proposal.data.kind : '');
  if (proposal.error) return <LoadFailure error={proposal.error} onRetry={() => void proposal.refetch()} />;
  if (template.error) return <LoadFailure error={template.error} onRetry={() => void template.refetch()} />;
  if (proposal.data?.mode === 'refine') return <Gone onStartOver={onStartOver} />;
  if (!proposal.data || !template.data) {
    return <p className="hint" aria-live="polite">Opening the proposal…</p>;
  }
  return <ReviewStep proposal={proposal.data} template={template.data.sections} onStartOver={onStartOver} />;
}

/** Expired, someone else's, or not an extraction at all (a refinement's id). */
function Gone({ onStartOver }: { onStartOver: () => void }) {
  return (
    <StateCard icon={<FileSearch aria-hidden="true" />} title="This extraction is no longer available">
      <p>Extractions are kept for 7 days, and only the person who started one can open it.</p>
      <StartAgain onStartOver={onStartOver} />
    </StateCard>
  );
}

function ExtractionStep({ id, onStartOver }: { id: string; onStartOver: () => void }) {
  const status = useExtractionStatus(id);
  if (status.error) {
    const gone = status.error instanceof ApiError && status.error.status === 404;
    return gone ? (
      <Gone onStartOver={onStartOver} />
    ) : (
      <LoadFailure error={status.error} onRetry={() => void status.refetch()} />
    );
  }
  const current = status.data?.status ?? 'queued';
  if (current === 'failed') {
    return (
      <StateCard warning icon={<AlertTriangle aria-hidden="true" />} title="The extraction failed">
        <p role="alert">{status.data?.error || 'The extraction failed.'}</p>
        <StartAgain onStartOver={onStartOver} />
      </StateCard>
    );
  }
  if (current === 'succeeded') return <Proposal id={id} onStartOver={onStartOver} />;
  return (
    <WaitingCard
      text={RUNNING_TEXT[current]}
      hint="You can leave this page; come back to New asset, From files, while the extraction is kept (7 days)."
    />
  );
}

export function FromFilesFlow() {
  const [params, setParams] = useSearchParams();
  const extractionId = params.get('extraction');
  const startOver = () => setParams({ from: 'files' });
  if (!extractionId) return <UploadStep onStarted={(id) => setParams({ from: 'files', extraction: id })} />;
  return <ExtractionStep id={extractionId} onStartOver={startOver} />;
}
